'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import ImageWrapper from '@/shared-components/ImageWrapper/ImageWrapper';
import { useValidator } from '@/hooks';
import type { Config, Fields } from '@/hooks/useValidator/interfaces';
import { VALIDITY } from '@/constants';
const Form = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Inputs/Form/Form'));
const Grid = dynamic(() => import('de-sxm-reactlib/components/atoms/Layout/Grid/Grid'));
const Button = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Button/Button'));
const Fieldset = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Inputs/Fieldset/Fieldset'));
import Input from 'de-sxm-reactlib-2/components/atoms/Inputs/Input/Input';
import styles from './emailcaptureclient.module.scss';

interface Props {
    asset: SiteEmailCapture;
    environment: string;
}

interface ServerFields {
    error: string;
    message: string;
    name: string;
}

type SubmitState = 'untouched' | 'failed' | 'success' | 'error' | 'duplicate';

export default function EmailCaptureClient({ asset, environment }: Props) {
    const { fields } = asset;
    const shortDescription = <RenderRTE contentfulDoc={fields.short_description} removePTag />;
    const legalText = <RenderRTE contentfulDoc={fields.legal_text} removePTag />;
    const confirmShortDesc = <RenderRTE contentfulDoc={fields.confirmation_short_description} removePTag />;
    const failShortDesc = <RenderRTE contentfulDoc={fields.failure_short_description} removePTag />;

    const validationConfig: Config = {
        fields: {
            email: [
                {
                    type: 'email',
                    helperText: 'Valid email address required',
                },
            ],
        },
    };

    const validator = useValidator(validationConfig);
    const [submitState, setSubmitState] = useState<SubmitState>('untouched');
    const [validations, setValidations] = useState(validator.getDefault);
    const [isSubmitting, setIsSubmitting] = useState(false);

    function validateInput(input: HTMLInputElement) {
        const newValidations = validator.validateField(input);
        const updatedValidations = { ...validations, ...newValidations };
        setValidations(updatedValidations);
        return updatedValidations.form.invalid === VALIDITY.valid;
    }

    function validateForm(event: React.SyntheticEvent) {
        const newValidations = validator.validate(event);
        const updatedValidations = { ...validations, ...newValidations };
        setValidations(updatedValidations);
        return updatedValidations.form.invalid === VALIDITY.valid;
    }

    function onSubmit(event: React.SyntheticEvent) {
        const isValid = validateForm(event);
        if (isValid) {
            setIsSubmitting(true);
        }
        return isValid;
    }

    async function formSuccess(response: Response) {
        setIsSubmitting(false);
        const json = await response.json();

        if (json.formSubmitResponse.status.message === 'ERROR') {
            const serverValidations: Fields = {};

            json.formSubmitResponse.errors.fields.forEach((field: ServerFields) => {
                serverValidations[field.name] = {
                    value: validations[field.name].value,
                    invalid: VALIDITY.invalid,
                    validations: { ...validations[field.name].validations },
                    helperText: { ...validations[field.name].helperText },
                };

                if (field.name === 'form') {
                    if (field.error === 'unknown') {
                        setSubmitState('failed');
                    }
                } else {
                    delete serverValidations.form;
                }
            });

            setValidations({ ...validations, ...serverValidations });
        } else if (json.formSubmitResponse.status.message === 'SUCCESS') {
            setSubmitState('success');
        }
    }

    function formFail() {
        setIsSubmitting(false);
        setSubmitState('failed');
    }

    function renderSubmitMessage() {
        if (submitState === 'success') {
            return (
                <>
                    <Grid container>
                        <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }} className={styles.confirmImg}>
                            <ImageWrapper
                                src={fields.image ? fields.image.url : ''}
                                alt={fields.image ? fields.image.altText : ''}
                                optimizedHeight={fields.image?.height}
                                optimizedWidth={fields.image?.width}
                            />
                        </Grid>
                        <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                            <div className={styles.headline}>{fields.confirmation_title}</div>
                        </Grid>
                        {confirmShortDesc && (
                            <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                                <div className={styles.subHeadline}>{confirmShortDesc}</div>
                            </Grid>
                        )}
                    </Grid>
                </>
            );
        }

        if (submitState === 'failed') {
            return (
                <>
                    <Grid container>
                        <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                            <div className={styles.headline}>{fields.failure_title}</div>
                        </Grid>
                        {failShortDesc && (
                            <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                                <div className={styles.subHeadline}>{failShortDesc}</div>
                            </Grid>
                        )}
                        <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }} className={styles.tryAgainRow}>
                            <Button
                                type="submit"
                                onClick={tryAgainClick}
                                ariaLabel="try again for email capture"
                                variant="secondary"
                                size="large"
                                dataCy="email-capture-try-again-button"
                            >
                                Try Again
                            </Button>
                        </Grid>
                    </Grid>
                </>
            );
        }
    }

    function tryAgainClick() {
        setSubmitState('untouched');
    }

    const ThreeDotLoader = () => {
        return (
            <div className={styles.loaderDots} aria-hidden="true">
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
            </div>
        );
    };

    return (
        <>
            <div aria-live="polite">{renderSubmitMessage()}</div>

            {submitState === 'untouched' ? (
                <Form
                    method="POST"
                    contentType="urlencoded"
                    onSubmit={onSubmit}
                    onBeforeSubmit={(data) => data}
                    onSuccess={formSuccess}
                    onFail={formFail}
                    onChange={validateForm}
                    novalidate={true}
                    dataCy="email-capture-form"
                    action="/p/formsubmit/SaveDataServlet"
                >
                    <input type="hidden" name="formId" value="8" />
                    <input type="hidden" name="environment" value={environment} />
                    <input type="hidden" name="evflag" value="" />
                    <input type="hidden" name="guid" value="" />
                    <input type="hidden" name="campaignid" value={fields.email_capture_id} />

                    <Grid container className={styles.emailContainer}>
                        {fields.eyebrow && (
                            <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                                <div className={styles.eyebrow}>{fields.eyebrow}</div>
                            </Grid>
                        )}
                        <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                            <div className={styles.headline}>{fields.title}</div>
                        </Grid>
                        {shortDescription && (
                            <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                                <div className={styles.subHeadline}>{shortDescription}</div>
                            </Grid>
                        )}
                        <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }} className={styles.formRow}>
                            <Fieldset theme="themeGrey" legend="">
                                <Input
                                    type="text"
                                    name="email"
                                    label="Enter your email address"
                                    invalid={validations.email.invalid}
                                    required={true}
                                    onBlur={(event) => validateInput(event.currentTarget)}
                                    invalidMessage={validations.email.helperText.email}
                                    theme="emailCapture"
                                    dataCy="email-capture-field"
                                    errorDataCy="email-capture-field-err"
                                />
                            </Fieldset>
                            <Button
                                type="submit"
                                ariaLabel="submit for email capture"
                                variant="primary"
                                size="large"
                                dataCy="email-capture-submit-button"
                                className={validations.email.invalid === 'invalid' ? styles.invaliddButton : ''}
                            >
                                {isSubmitting ? <ThreeDotLoader /> : fields.cta_label}
                            </Button>
                        </Grid>
                        {legalText && (
                            <Grid col={{ xs: 12, lg: 10 }} start={{ lg: 2 }}>
                                <div className={styles.legalText}>{legalText}</div>
                            </Grid>
                        )}
                    </Grid>
                </Form>
            ) : undefined}
        </>
    );
}
